import { useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './api/auth.js';
import { Loading } from './components/ui.js';
import { Icon, type IconName } from './components/icons.js';
import { LoginPage } from './pages/LoginPage.js';
import { SetupPage } from './pages/SetupPage.js';
import { OverviewPage } from './pages/OverviewPage.js';
import { SchoolsPage } from './pages/SchoolsPage.js';
import { DevicesPage } from './pages/DevicesPage.js';
import { DeviceDetailPage } from './pages/DeviceDetailPage.js';
import { AudioPage } from './pages/AudioPage.js';
import { SchedulesPage } from './pages/SchedulesPage.js';
import { ScheduleEditorPage } from './pages/ScheduleEditorPage.js';
import { CalendarPage } from './pages/CalendarPage.js';
import { DeploymentsPage } from './pages/DeploymentsPage.js';
import { PlaybackPage } from './pages/PlaybackPage.js';
import { EmergencyPage } from './pages/EmergencyPage.js';
import { UsersPage } from './pages/UsersPage.js';
import { AuditPage } from './pages/AuditPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { FirmwarePage } from './pages/FirmwarePage.js';
import { SchoolBoard } from './pages/SchoolBoard.js';
import { DisplayScreen } from './pages/DisplayScreen.js';
import { BoardEditorPage } from './pages/BoardEditorPage.js';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /** Hidden for users without it, so a view-only account never lands on a refusal. */
  permission?: string;
}

/*
 * The rail is grouped by what an operator is doing, not by API shape: what the
 * directorate owns, what it schedules, and what it administers. Emergency is
 * deliberately outside every group (§7).
 */
const NAV: Array<{ group: string; items: NavItem[] }> = [
  {
    group: 'المتابعة',
    items: [
      { to: '/', label: 'نظرة عامة', icon: 'overview', end: true, permission: 'device.read' },
      { to: '/board', label: 'لوحة المدرسة', icon: 'schools', permission: 'school.read' },
      { to: '/notices', label: 'لوحة الإعلانات', icon: 'audio', permission: 'school.manage' },
      { to: '/deployments', label: 'متابعة النشر', icon: 'deployments', permission: 'schedule.publish' },
      { to: '/audit', label: 'سجل التدقيق', icon: 'audit', permission: 'audit.read' },
    ],
  },
  {
    group: 'التشغيل',
    items: [
      { to: '/schedules', label: 'الجداول', icon: 'schedules', permission: 'schedule.read' },
      { to: '/calendar', label: 'الاستثناءات والعطل', icon: 'calendar', permission: 'schedule.read' },
      { to: '/audio', label: 'مكتبة الأصوات', icon: 'audio', permission: 'audio.read' },
      { to: '/playback', label: 'التشغيل اليدوي', icon: 'playback', permission: 'playback.manual' },
    ],
  },
  {
    group: 'الإدارة',
    items: [
      { to: '/schools', label: 'المدارس والمجموعات', icon: 'schools', permission: 'school.read' },
      { to: '/devices', label: 'الأجهزة', icon: 'devices', permission: 'device.read' },
      { to: '/firmware', label: 'تحديث برنامج الأجهزة', icon: 'deployments', permission: 'firmware.manage' },
      { to: '/users', label: 'المستخدمون', icon: 'users', permission: 'user.read' },
      { to: '/settings', label: 'الإعدادات', icon: 'settings' },
    ],
  },
];

/** School-scoped accounts get the school board instead of the directorate console. */
const SCHOOL_BOARD_ROLES = new Set(['SCHOOL_ADMIN', 'VIEWER']);

export function App(): JSX.Element {
  const { me, loading, can } = useAuth();
  const location = useLocation();
  /*
   * Narrow screens collapse the rail behind a toggle: eleven destinations laid
   * out as a wrapped strip pushed the actual page most of a screen down. It is
   * closed on every navigation so the content is what you land on.
   */
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [location.pathname]);

  // The hall screen is opened by a public link: no session, no rail, no controls.
  if (location.pathname.startsWith('/display/')) {
    return (
      <Routes>
        <Route path="/display/:token" element={<DisplayScreen />} />
      </Routes>
    );
  }

  if (loading) {
    return (
      <div className="login-shell">
        <Loading label="جارٍ التحقق من الجلسة…" />
      </div>
    );
  }

  if (!me) {
    return (
      <Routes>
        <Route path="/setup" element={<SetupPage />} />
        <Route path="*" element={<LoginPage />} />
      </Routes>
    );
  }

  if (me.role && SCHOOL_BOARD_ROLES.has(me.role) && me.school_scope.length > 0) {
    return (
      <Routes>
        <Route path="*" element={<SchoolBoard />} />
      </Routes>
    );
  }

  if (location.pathname === '/board' || location.pathname.startsWith('/board/')) {
    return (
      <Routes>
        <Route path="/board" element={<SchoolBoard preview />} />
        <Route path="/board/:schoolId" element={<SchoolBoard preview />} />
      </Routes>
    );
  }

  return (
    <div className="app">
      <nav className={`sidebar ${navOpen ? 'open' : ''}`} aria-label="التنقل الرئيسي">
        <div className="brand">
          <span className="brand-name">منظومة الإذاعة المدرسية</span>
          <button
            type="button"
            className="nav-toggle ghost"
            aria-expanded={navOpen}
            aria-controls="rail-links"
            onClick={() => setNavOpen((open) => !open)}
          >
            <Icon name={navOpen ? 'close' : 'menu'} size={20} />
            <span className="sr-only">{navOpen ? 'إغلاق القائمة' : 'فتح القائمة'}</span>
          </button>
        </div>
        <div className="rail-links" id="rail-links">
        {NAV.map((section) => ({
          ...section,
          items: section.items.filter((item) => !item.permission || can(item.permission)),
        }))
          .filter((section) => section.items.length > 0)
          .map((section) => (
          <div key={section.group} className="nav-section">
            <div className="nav-group">{section.group}</div>
            {section.items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end ?? false}
                className={({ isActive }) => (isActive ? 'active' : '')}
              >
                <Icon name={item.icon} />
                {item.label}
              </NavLink>
            ))}
          </div>
        ))}
        {/* The emergency console is deliberately separate and visually distinct (§7). */}
        <NavLink
          to="/emergency"
          className={({ isActive }) => `emergency ${isActive ? 'active' : ''}`}
        >
          <Icon name="emergency" />
          الطوارئ والإخلاء
        </NavLink>
        </div>

        <div className="spacer" />
        <div className="footer">
          <span className="avatar" aria-hidden="true">
            {initial(me.user.display_name)}
          </span>
          <span className="who">
            <span className="name">{me.user.display_name}</span>
            <span className="mail">{me.user.email}</span>
          </span>
        </div>
      </nav>

      <div className="main">
        <Header />
        <main className="content">
          <Routes>
            <Route path="/" element={<OverviewPage />} />
            <Route path="/schools" element={<SchoolsPage />} />
            <Route path="/notices" element={<BoardEditorPage />} />
            <Route path="/devices" element={<DevicesPage />} />
            <Route path="/devices/:id" element={<DeviceDetailPage />} />
            <Route path="/firmware" element={<FirmwarePage />} />
            <Route path="/audio" element={<AudioPage />} />
            <Route path="/schedules" element={<SchedulesPage />} />
            <Route path="/schedules/new" element={<ScheduleEditorPage />} />
            <Route path="/schedules/:id" element={<ScheduleEditorPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/deployments" element={<DeploymentsPage />} />
            <Route path="/deployments/:id" element={<DeploymentsPage />} />
            <Route path="/playback" element={<PlaybackPage />} />
            <Route path="/emergency" element={<EmergencyPage />} />
            <Route path="/users" element={<UsersPage />} />
            <Route path="/audit" element={<AuditPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/setup" element={<Navigate to="/" replace />} />
            <Route
              path="*"
              element={
                <div className="empty">
                  الصفحة غير موجودة: <span className="mono">{location.pathname}</span>
                </div>
              }
            />
          </Routes>
        </main>
      </div>
    </div>
  );
}

function Header(): JSX.Element {
  const { me, logout, switchTenant } = useAuth();
  const { pathname } = useLocation();
  if (!me) return <div />;

  const tenant = me.tenants.find((t) => t.id === me.active_tenant_id);

  return (
    <header className="topbar">
      <div className="titles">
        <h1>{pageTitle(pathname)}</h1>
        {tenant ? <span className="tenant">{tenant.name}</span> : null}
      </div>
      {me.tenants.length > 1 ? (
        <select
          aria-label="اختيار المديرية"
          value={me.active_tenant_id ?? ''}
          onChange={(e) => void switchTenant(e.target.value)}
          style={{ width: 'auto' }}
        >
          {me.tenants.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name}
            </option>
          ))}
        </select>
      ) : null}
      <div className="grow" />
      {me.role ? <span className="role">{roleLabel(me.role)}</span> : null}
      <button className="ghost" onClick={() => void logout()}>
        <Icon name="logout" size={16} />
        تسجيل الخروج
      </button>
    </header>
  );
}

function roleLabel(role: string): string {
  const map: Record<string, string> = {
    PLATFORM_ADMIN: 'مسؤول المنصة',
    TENANT_ADMIN: 'مدير المديرية',
    TENANT_OPERATOR: 'مشغّل المديرية',
    SCHOOL_ADMIN: 'مسؤول المدرسة',
    VIEWER: 'مراقب',
  };
  return map[role] ?? role;
}

/**
 * Heading for the current route. Longest match wins so a detail route such as
 * /devices/:id still reads as the devices section rather than falling through.
 */
function pageTitle(pathname: string): string {
  if (pathname === '/') return 'نظرة عامة';
  if (pathname.startsWith('/emergency')) return 'الطوارئ والإخلاء';

  let best = '';
  let label = 'المنصة';
  for (const section of NAV) {
    for (const item of section.items) {
      if (item.to !== '/' && pathname.startsWith(item.to) && item.to.length > best.length) {
        best = item.to;
        label = item.label;
      }
    }
  }
  return label;
}

/** First letter of the display name, for the rail avatar. */
function initial(name: string): string {
  return [...name.trim()][0] ?? '؟';
}
